import { useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";

export interface DateRange {
  from: Date | undefined;
  to: Date | undefined;
}

function parseDate(str: string | null): Date | null {
  if (!str) return null;
  const match = str.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return null;
  const d = new Date(+match[1], +match[2] - 1, +match[3]);
  return isNaN(d.getTime()) ? null : d;
}

function formatDate(d: Date): string {
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

function isToday(d: Date): boolean {
  const now = new Date();
  return (
    d.getFullYear() === now.getFullYear() &&
    d.getMonth() === now.getMonth() &&
    d.getDate() === now.getDate()
  );
}

export function useDateParams() {
  const [searchParams, setSearchParams] = useSearchParams();

  const date = useMemo(
    () => parseDate(searchParams.get("date")) ?? new Date(),
    [searchParams],
  );

  const dateRange = useMemo((): DateRange => {
    const from = parseDate(searchParams.get("from"));
    const to = parseDate(searchParams.get("to"));
    return { from: from ?? undefined, to: to ?? undefined };
  }, [searchParams]);

  const setDate = useCallback(
    (d: Date) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        if (isToday(d)) {
          next.delete("date");
        } else {
          next.set("date", formatDate(d));
        }
        return next;
      }, { replace: true });
    },
    [setSearchParams],
  );

  const setDateRange = useCallback(
    (range: DateRange) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        if (range.from) next.set("from", formatDate(range.from));
        else next.delete("from");
        if (range.to) next.set("to", formatDate(range.to));
        else next.delete("to");
        if (range.from || range.to) next.delete("date");
        // Clearing range entirely resets to today (default)
        if (!range.from && !range.to) next.delete("date");
        return next;
      }, { replace: true });
    },
    [setSearchParams],
  );

  return { date, dateRange, setDate, setDateRange };
}
